import React, { FC, useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Backdrop,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { ViewsMenuSettingsStyles } from "./viewsMenuSettingsStyles";
import { TFunction } from "i18next";

interface SavedView {
  id: string;
  name: string;
  savedDate: string;
  filters?: any;
  configuration?: any;
  dataViews?: any[];
  createdAt?: string;
  version?: string;
}

interface CreateViewModalProps {
  open: boolean;
  viewName: string;
  viewDescription: string;
  onClose: () => void;
  onViewNameChange: (value: string) => void;
  onViewDescriptionChange: (value: string) => void;
  onSave: () => void;
  isLoading?: boolean;
  viewWithFilters: boolean;
  t: TFunction<"global", undefined>;
  existingViews?: SavedView[];
}

const CreateViewModal: FC<CreateViewModalProps> = ({
  open,
  viewName,
  viewDescription,
  onClose,
  onViewNameChange,
  onViewDescriptionChange,
  onSave,
  isLoading = false,
  viewWithFilters,
  t,
  existingViews = [],
}) => {
  const [nameError, setNameError] = useState<string>("");

  const validateViewName = (name: string) => {
    if (!name.trim()) {
      setNameError(t("Data_views.enter_name"));
      return false;
    }

    if (name.length > 30) {
      setNameError(t("Data_views.name_too_long"));
      return false;
    }

    const isDuplicate = existingViews.some(
      (view) => view.name.toLowerCase().trim() === name.toLowerCase().trim()
    );

    if (isDuplicate) {
      setNameError(t("Data_views.name_already_exists"));
      return false;
    }

    setNameError("");
    return true;
  };

  useEffect(() => {
    if (!open) {
      setNameError("");
    }
  }, [open]);

  const handleViewNameChange = (value: string) => {
    const limitedValue = value.slice(0, 30);
    onViewNameChange(limitedValue);

    if (limitedValue.trim()) {
      validateViewName(limitedValue);
    } else {
      setNameError("");
    }
  };

  const handleSave = () => {
    if (validateViewName(viewName)) {
      onSave();
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={ViewsMenuSettingsStyles.dialogTitle}>
        {t("Data_views.save_new_view")}
      </DialogTitle>
      <DialogContent>
        <Box sx={ViewsMenuSettingsStyles.contentContainer}>
          <Tooltip title={t("Data_views.saved_views_desc_modal")}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={viewWithFilters}
                    disabled={true}
                    size="small"
                  />
                }
                label={
                  <Box sx={ViewsMenuSettingsStyles.switchLabelContainer}>
                    <Typography
                      variant="body2"
                      sx={ViewsMenuSettingsStyles.switchText}
                    >
                      {t("Data_views.view_with_filters")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={ViewsMenuSettingsStyles.comingSoonBadge}
                    >
                      {t("Data_views.coming_soon")}
                    </Typography>
                  </Box>
                }
                sx={ViewsMenuSettingsStyles.switchFormControl}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box sx={ViewsMenuSettingsStyles.fieldsContainer}>
          <TextField
            autoFocus
            fullWidth
            label={t("Data_views.view_name") + " *"}
            variant="outlined"
            value={viewName}
            onChange={(e) => handleViewNameChange(e.target.value)}
            placeholder={t("Data_views.enter_name")}
            disabled={isLoading}
            error={!!nameError}
            helperText={nameError || `${viewName.length}/30 caracteres`}
            sx={ViewsMenuSettingsStyles.nameField}
          />
          <TextField
            fullWidth
            label={t("Data_views.description")}
            variant="outlined"
            multiline
            rows={3}
            value={viewDescription}
            onChange={(e) => onViewDescriptionChange(e.target.value)}
            placeholder={t("Data_views.placeholder")}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={ViewsMenuSettingsStyles.dialogActions}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
          sx={ViewsMenuSettingsStyles.cancelButton}
        >
          {t("Common_options.Cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !!nameError || !viewName.trim()}
          sx={ViewsMenuSettingsStyles.saveButton}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            t("Common_options.Save")
          )}
        </Button>
      </DialogActions>

      {/* Loading overlay */}
      <Backdrop sx={ViewsMenuSettingsStyles.backdrop} open={isLoading}>
        <Box sx={ViewsMenuSettingsStyles.loadingContainer}>
          <CircularProgress color="inherit" />
          <Typography sx={ViewsMenuSettingsStyles.loadingText}>
            <CircularProgress size={24} />
          </Typography>
        </Box>
      </Backdrop>
    </Dialog>
  );
};

export default CreateViewModal;
